/**
 * Ditado → prontuário estruturado (spec 009, pedido de 30/09/2026).
 *
 * "Ao concluir um ditado, as informações contidas no ditado sejam incluídas
 * nos exames do atendimento e/ou num texto formatado estruturado de
 * prontuário oftalmológico." Até aqui o ditado só levava os VALORES de exame
 * (card "Exames do atendimento") e guardava a transcrição crua como evolução.
 *
 * Agora, ao concluir ("Registrar no prontuário" / botão "Prontuário"), UMA
 * chamada de IA (`formatText`, a mesma do "Formatar" do Extrair Dados) lê a
 * transcrição e devolve a `evolucao` (queixa, HMA, antecedentes, achados,
 * hipóteses/CID, conduta). A partir daí é tudo determinístico:
 *  - queixa/HDA/antecedentes/hipótese/CID/conduta → módulos do atendimento
 *    (`buildEvolucaoModuleFill` + conduta), não-destrutivo;
 *  - o texto do Prontuário é COMPOSTO dos módulos por `composeEvolucao` —
 *    valores de exame saem EXATOS dos módulos, a IA só escreve a narrativa
 *    (regra dos laudos: IA nunca parafraseia medida).
 *
 * O catálogo de módulos vai no pedido: é o gatilho da regra do backend que
 * tira os números da narrativa ("NÃO repita em exameFisico as medidas…"),
 * senão a PIO sairia duas vezes — uma exata, outra do jeito da IA.
 */

import { serializeCatalogForAI } from '@/utils/encounterSerialize';
import { applyExamFill, type ExamFillMap } from '@/utils/encounterAutoFill';
import { buildEvolucaoModuleFill, composeEvolucao } from '@/utils/composeEvolucao';
import { REGRAS_QUEM_DISSE } from '@/utils/quemDisse';
import type { AIEvolucao } from '@/types/ai';
import type { EncounterExamsState, EncounterTypeDef, ExamModuleDef } from '@/types/encounter';
import { fitRefractionToRecord, LEGACY_REFRACAO_MODULE_ID, VOICE_APPLY_MODULES } from './examFill';
import { returnLine, surgeryLine } from './normalize';
import { soaParecido } from './grounding';
import { todayLocalISO } from '@/services/session/audioSessionStore';
import type { VoiceDraft } from './resolveDrafts';

/**
 * O que aconteceu com o Prontuário ao concluir o ditado:
 * gravado (no editor e no histórico) · aplicado (no editor, gravação falhou) ·
 * mantido (o médico já tinha escrito lá) · nenhum (nada a compor) · erro (IA falhou).
 */
export type VoiceProntuarioOutcome = 'gravado' | 'aplicado' | 'mantido' | 'nenhum' | 'erro';

/**
 * Os cards do ditado em texto, pra IA do prontuário (pedido de 05/10/2026).
 *
 * Campo: prescreveu OPTI e a Conduta saiu "Patanol S ou Optrex" — a IA só
 * lia a transcrição do serviço ("Ouvido"), que erra nome de remédio, e
 * nunca via a receita que estava na tela. Os cards já passaram pelo banco
 * de medicamentos e pela conferência; são eles que dizem o que foi
 * prescrito. "Exames do atendimento" fica de fora: os valores entram nos
 * módulos direto, sem IA.
 */
export function describeVoiceDraftsForRecord(drafts: VoiceDraft[]): string {
  const linhas: string[] = [];
  for (const d of drafts) {
    switch (d.kind) {
      case 'receitaMedicamentos': {
        const itens = d.items.map((i) => {
          const m = i.match.value;
          if (!m) return `- "${i.match.heard}" (nome ainda não confirmado na receita)`;
          const partes = [m.nome, m.dosagem, m.posologia, m.duracao, m.olho].map((s) => (s ?? '').trim()).filter(Boolean);
          return `- ${partes.join(' — ')}`;
        });
        if (itens.length === 0) break;
        linhas.push(`Receita de medicamentos (todos prescritos juntos):\n${itens.join('\n')}`);
        if (d.notes?.trim()) linhas.push(`Orientações da receita: ${d.notes.trim()}`);
        break;
      }
      case 'receitaOculos':
        // Os graus vão exatos pra refração dos módulos; na conduta, só o fato.
        linhas.push('Receita de óculos prescrita (graus já registrados na refração — não repita os números).');
        break;
      case 'solicitacaoExames': {
        const exames = d.exams.map((e) => e.nome.trim()).filter(Boolean);
        if (exames.length > 0) linhas.push(`Exames solicitados: ${exames.join('; ')}`);
        if (d.notes?.trim()) linhas.push(`Orientações do pedido de exames: ${d.notes.trim()}`);
        if (d.surgeries.length > 0) linhas.push(`Cirurgia/procedimento indicado: ${d.surgeries.map(surgeryLine).join('; ')}`);
        if (d.surgeryNotes?.trim()) linhas.push(`Observações da cirurgia: ${d.surgeryNotes.trim()}`);
        if (d.retorno) linhas.push(`Retorno: ${returnLine(d.retorno) || d.retorno.motivo}`);
        break;
      }
      case 'examesAtendimento':
        break;
      case 'atestadoLaudo':
        // Só o fato (o texto é o modelo da clínica, não conduta).
        if (d.modelo) linhas.push(`Documento entregue: ${d.modelo.title}${d.dias ? ` (${d.dias} dia(s))` : ''}`);
        break;
    }
  }
  return linhas.join('\n');
}

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Nome do remédio no texto da IA = nome do card (pedido de 15/10/2026).
 *
 * Campo: a receita saiu "Xalacom" (o banco corrigiu o "Chalakom" da
 * transcrição), mas a HDA e a Conduta saíram "Chalakom". A regra no pedido
 * só vale pra conduta e a IA nem sempre obedece ("manter uso" ela leu como
 * remédio em uso). Então a troca é aqui, sem IA: em todo campo da evolução,
 * o nome ouvido (ou grafia que soa igual, com maiúscula — nome próprio) vira
 * o nome do card. Só remédio CONFIRMADO no card; o resto fica como foi dito.
 */
export function fixMedicationNames(ev: AIEvolucao | null, drafts: VoiceDraft[]): AIEvolucao | null {
  if (!ev) return ev;
  const meds: Array<{ nome: string; ouvido: string }> = [];
  for (const d of drafts) {
    if (d.kind !== 'receitaMedicamentos') continue;
    for (const i of d.items) {
      const nome = i.match.value?.nome?.trim();
      if (nome) meds.push({ nome, ouvido: i.match.heard.trim() });
    }
  }
  if (meds.length === 0) return ev;
  const nomes = new Set(meds.map((m) => semAcento(m.nome)));
  const palavras = (s: string) => s.split(/\s+/).filter(Boolean).length;
  const maxJanela = Math.max(...meds.flatMap((m) => [palavras(m.nome), palavras(m.ouvido)]));

  // Só a 1ª palavra pode soar parecido; o resto do nome tem que estar igual
  // ("Patanol 1x" não é "Patanol S").
  const soaComoNome = (nome: string, janela: string[]): boolean => {
    const partes = nome.split(/\s+/).filter(Boolean);
    if (partes.length !== janela.length) return false;
    return soaParecido(partes[0], janela[0]) && partes.slice(1).every((p, k) => semAcento(p) === semAcento(janela[k + 1]));
  };

  const corrige = (texto: string | undefined): string | undefined => {
    if (!texto) return texto;
    const tokens = [...texto.matchAll(/[\p{L}\p{N}]+/gu)].map((m) => ({ t: m[0], ini: m.index!, fim: m.index! + m[0].length }));
    let out = '';
    let cursor = 0;
    let i = 0;
    while (i < tokens.length) {
      let avanco = 1;
      for (let n = Math.min(maxJanela, tokens.length - i); n >= 1; n -= 1) {
        const janela = tokens.slice(i, i + n);
        // Janela só de palavras separadas por espaço ("Patanol S", não "Patanol, S").
        if (janela.some((tk, k) => k > 0 && texto.slice(janela[k - 1].fim, tk.ini).trim() !== '')) continue;
        const frase = janela.map((tk) => tk.t).join(' ');
        const chave = semAcento(frase);
        if (nomes.has(chave)) { avanco = n; break; } // já está certo
        const maiuscula = /^\p{Lu}/u.test(frase);
        const alvos = meds.filter(
          (m) =>
            (m.ouvido.length >= 4 && semAcento(m.ouvido) === chave) ||
            (maiuscula && soaComoNome(m.nome, janela.map((tk) => tk.t))),
        );
        const unicos = [...new Set(alvos.map((m) => m.nome))];
        if (unicos.length === 1) {
          out += texto.slice(cursor, janela[0].ini) + unicos[0];
          cursor = janela[n - 1].fim;
          avanco = n;
          break;
        }
      }
      i += avanco;
    }
    return out + texto.slice(cursor);
  };

  return {
    ...ev,
    queixaPrincipal: corrige(ev.queixaPrincipal) ?? ev.queixaPrincipal,
    historiaAtual: corrige(ev.historiaAtual) ?? ev.historiaAtual,
    antecedentesPessoais: corrige(ev.antecedentesPessoais),
    antecedentesFamiliares: corrige(ev.antecedentesFamiliares),
    exameFisico: corrige(ev.exameFisico) ?? ev.exameFisico,
    hipotesesDiagnosticas: (ev.hipotesesDiagnosticas ?? []).map((h) => corrige(h) ?? h),
    conduta: corrige(ev.conduta) ?? ev.conduta,
  };
}

/**
 * Anamnese por extenso (pedido de 07/10/2026: "está ficando muito resumida").
 * A queixa fica curta — é o título; o detalhe vai TODO na HDA, que agora tem
 * módulo próprio. Resumir aqui é perder o relato. Mas a HDA é só o RELATO
 * (25/10/2026): o que o médico disse na conversa vai nos campos dele
 * (`REGRAS_QUEM_DISSE`).
 */
const ANAMNESE_DETALHADA =
  'ANAMNESE — NÃO RESUMA:\n' +
  '- "queixaPrincipal": o motivo da consulta em 1 frase curta, como o paciente ou o acompanhante trouxe, com o olho e o tempo ' +
  '(ex.: "Embaçamento visual em OD há 6 meses").\n' +
  '- "historiaAtual": o RELATO COMPLETO em texto corrido, terceira pessoa ("Paciente refere…"; do acompanhante: ' +
  '"Filha relata…"), com TODOS os detalhes que o paciente ou o acompanhante contou: início e tempo de evolução, olho(s) ' +
  'acometido(s), se piora ou melhora e com o quê, longe/perto, dia/noite, sintomas associados (dor, vermelhidão, ardência, ' +
  'lacrimejamento, coceira, fotofobia, ofuscamento, flashes, moscas volantes, visão dupla, cefaleia), negativas ' +
  'pertinentes ("nega dor"), tratamentos e colírios já usados e o resultado, uso de óculos/lentes de contato, exames ou ' +
  'consultas anteriores, e o impacto no dia a dia (leitura, direção, trabalho). Pode ter vários parágrafos. Não corte ' +
  'nada do relato; também não acrescente nada que não foi dito.\n' +
  '- Doenças, cirurgias, remédios em uso e alergias ditos vão em "antecedentesPessoais" (com doses quando ditas); ' +
  'doença de parente vai em "antecedentesFamiliares".';

/**
 * Transcrição do Ao vivo marcada por quem falou (25/10/2026,
 * `marcarFalantes`): os rótulos são medida dos microfones, mas o médico
 * ditando o relato continua sendo relato.
 */
const MARCACAO_FALANTES =
  'Cada linha do ditado começa com QUEM FALOU, medido pelos microfones (lapela do médico × microfone da sala), ' +
  'não por IA: [Médico]; [Paciente/acompanhante] (os microfones não separam os dois — decida pelo conteúdo); ' +
  '[A → B] quando os dois falaram naquele trecho, nessa ordem (ex.: "Não." do paciente colado na pergunta ' +
  'seguinte do médico); [?] sem medida. Use os rótulos para separar o relato da fala do médico, junto com as ' +
  'regras de QUEM DISSE: o que está em [Paciente/acompanhante] é relato; o que está em [Médico] não é relato, ' +
  'a não ser quando ele dita o relato ("paciente refere…") ou repete o que o paciente contou. Não escreva os ' +
  'rótulos no prontuário.';

/**
 * Texto mandado ao `formatText`: o ditado, os cards e o catálogo dos módulos
 * de medição. `marcada`: a mesma transcrição com quem falou em cada linha.
 */
export function buildVoiceRecordText(transcript: string, drafts: VoiceDraft[] = [], marcada?: string | null): string {
  const docs = describeVoiceDraftsForRecord(drafts);
  const comFalantes = !!marcada?.trim();
  return [
    'Transcrição AUTOMÁTICA de uma consulta OFTALMOLÓGICA, captada durante o atendimento: é a CONVERSA ' +
      'entre médico, paciente e às vezes acompanhante, misturada com o que o médico dita para o sistema' +
      (comFalantes ? '. ' : ', sem marcar quem fala. ') +
      'Monte a evolução com o que foi DITO — ' +
      'não invente queixa, antecedente, achado nem diagnóstico que não tenham sido falados ' +
      '(CID só o do diagnóstico dito). ' +
      'Pedidos de receita, óculos, exames, cirurgia e retorno entram RESUMIDOS em "conduta".',
    ANAMNESE_DETALHADA,
    REGRAS_QUEM_DISSE,
    comFalantes
      ? `### Ditado (cada fala com quem falou)\n${MARCACAO_FALANTES}\n\n${marcada!.trim()}`
      : `### Ditado\n${transcript.trim()}`,
    ...(docs
      ? [
          '### Documentos preparados neste ditado (conferidos pelo médico na tela — FONTE DA VERDADE da conduta)\n' +
            `${docs}\n\n` +
            'Regras para estes documentos:\n' +
            '- O ditado acima é transcrição AUTOMÁTICA e erra nomes, sobretudo de remédio e exame. Na conduta, ' +
            'escreva remédios, doses, posologias, exames, cirurgia e retorno EXATAMENTE como estão nestes documentos, ' +
            'mesmo que o ditado traga outro nome ou outra grafia parecida.\n' +
            '- Tudo o que está listado foi prescrito/pedido JUNTO: nunca transforme itens em alternativas ("X ou Y").\n' +
            '- Não cite como prescrito/pedido nenhum remédio, exame ou cirurgia que não esteja nestes documentos. ' +
            'Remédio que o ditado cita como em uso, suspenso ou substituído pode aparecer assim (ex.: "no lugar de…").',
        ]
      : []),
    `### Módulos de exame disponíveis (preencha "examesAtendimento" SÓ com estes — use a CHAVE exata entre aspas; valores EXATOS como ditos, sem unidade; só o que estiver EXPLÍCITO; campo "por olho" → OD/OE só do lado citado; "valor único" → mono):\n${serializeCatalogForAI()}`,
  ].join('\n\n');
}

/** Evolução da IA → módulos (queixa, antecedentes, hipótese, CID e conduta). */
export function evolucaoToModuleFill(ev: AIEvolucao | null | undefined): ExamFillMap {
  const fill = buildEvolucaoModuleFill(ev);
  const conduta = ev?.conduta?.trim();
  if (conduta) fill['conduta'] = { conduta: { mono: conduta } };
  return fill;
}

export interface VoiceRecordInput {
  /** Estado atual dos módulos do atendimento. */
  state: EncounterExamsState;
  /** Valores já validados pelo ditado (card de exames + refração da receita). Mandam. */
  cardFill: ExamFillMap;
  /** Valores de exame que a IA leu na transcrição. `null` quando há card
   *  (o card já passou pela ancoragem; a IA não repõe o que ele tirou). */
  aiExamFill: ExamFillMap | null;
  /** Evolução estruturada da IA. `null` = a IA falhou / não rodou. */
  evolucao: AIEvolucao | null;
  catalog: ExamModuleDef[];
  encounterTypes?: EncounterTypeDef[];
  /** Conduta digitada no Documento de Texto (drafts.conduta). */
  condutaDraft?: string;
  /**
   * O que um "Registrar" anterior DESTE paciente escreveu nos módulos da
   * evolução (`VoiceRecordPlan.voiceFill`). Célula que ainda tem exatamente
   * esse valor não foi mexida pelo médico — e o ditado novo pode trocá-la.
   */
  lastVoiceFill?: ExamFillMap | null;
}

export interface VoiceRecordPlan {
  /** Próximo estado dos módulos. */
  next: EncounterExamsState;
  /** Células novas preenchidas neste merge. */
  filledCount: number;
  /** Valores de exame (medição) que entraram no merge — pra contagem na tela. */
  examFill: ExamFillMap;
  /** Evolução composta dos módulos ('' quando não há nada estruturado). */
  prontuario: string;
  /** O que, nos módulos da evolução, está como o ditado escreveu — guardar
   *  e devolver no próximo "Registrar" como `lastVoiceFill`. */
  voiceFill: ExamFillMap;
}

type Lado = 'OD' | 'OE' | 'mono';
const LADOS: Lado[] = ['OD', 'OE', 'mono'];

function valorNaFicha(state: EncounterExamsState, mod: string, campo: string, lado: Lado): string {
  return (state.entries.find((e) => e.moduleId === mod)?.values[campo]?.[lado] ?? '').trim();
}

function poeCelula(fill: ExamFillMap, mod: string, campo: string, lado: Lado, valor: string): void {
  ((fill[mod] ??= {})[campo] ??= {})[lado] = valor;
}

/**
 * Separa a evolução nova em o que pode TROCAR (a célula ainda é exatamente o
 * que o ditado pôs da última vez — o médico não mexeu) e o resto, que só
 * entra onde está vazio. Pedido de 05/10/2026: o segundo "Registrar" troca a
 * conduta (a primeira tinha saído com remédio errado).
 */
function separaTrocaveis(
  state: EncounterExamsState,
  fill: ExamFillMap,
  ultimo: ExamFillMap | null | undefined,
): { troca: ExamFillMap; resto: ExamFillMap } {
  const troca: ExamFillMap = {};
  const resto: ExamFillMap = {};
  for (const [mod, campos] of Object.entries(fill)) {
    for (const [campo, celula] of Object.entries(campos)) {
      for (const lado of LADOS) {
        const novo = celula[lado];
        if (novo === undefined) continue;
        const atual = valorNaFicha(state, mod, campo, lado);
        const antes = ultimo?.[mod]?.[campo]?.[lado]?.trim();
        const doDitado = atual !== '' && antes !== undefined && atual === antes;
        poeCelula(doDitado ? troca : resto, mod, campo, lado, novo);
      }
    }
  }
  return { troca, resto };
}

/** Células de `candidatos` que estão na ficha exatamente com aquele valor. */
function iguaisNaFicha(state: EncounterExamsState, candidatos: ExamFillMap[]): ExamFillMap {
  const out: ExamFillMap = {};
  for (const fill of candidatos) {
    for (const [mod, campos] of Object.entries(fill)) {
      for (const [campo, celula] of Object.entries(campos)) {
        for (const lado of LADOS) {
          const v = celula[lado]?.trim();
          if (v && valorNaFicha(state, mod, campo, lado) === v) poeCelula(out, mod, campo, lado, v);
        }
      }
    }
  }
  return out;
}

/**
 * Junta tudo, sem IA: exames (card manda, IA completa) → módulos de anamnese
 * e diagnóstico → texto do Prontuário. Nada sobrescreve o que o médico digitou;
 * o que um "Registrar" anterior escreveu e ninguém mexeu, o novo atualiza.
 */
export function planVoiceRecord(input: VoiceRecordInput): VoiceRecordPlan {
  const examFill: ExamFillMap = { ...input.cardFill };
  for (const [mod, fields] of Object.entries(input.aiExamFill ?? {})) {
    examFill[mod] = { ...fields, ...(examFill[mod] ?? {}) };
  }
  const opts = { autoAdd: true, overwrite: false } as const;
  // Ficha com o bloco antigo de refração: a subjetiva entra nele.
  const catalog = input.catalog.some((m) => m.id === LEGACY_REFRACAO_MODULE_ID)
    ? input.catalog
    : [...input.catalog, ...VOICE_APPLY_MODULES.filter((m) => m.id === LEGACY_REFRACAO_MODULE_ID)];
  const a = applyExamFill(input.state, fitRefractionToRecord(examFill, input.state), catalog, opts);
  const evolucaoFill = evolucaoToModuleFill(input.evolucao);
  const { troca, resto } = separaTrocaveis(a.next, evolucaoFill, input.lastVoiceFill);
  const t = applyExamFill(a.next, troca, input.catalog, { autoAdd: true, overwrite: true });
  const b = applyExamFill(t.next, resto, input.catalog, opts);
  const prontuario = composeEvolucao(b.next, {
    encounterTypes: input.encounterTypes,
    historiaAtual: input.evolucao?.historiaAtual,
    exameFisico: input.evolucao?.exameFisico,
    conduta: input.condutaDraft,
  });
  return {
    next: b.next,
    filledCount: a.filledCount + t.filledCount + b.filledCount,
    examFill,
    prontuario,
    voiceFill: iguaisNaFicha(b.next, [input.lastVoiceFill ?? {}, evolucaoFill]),
  };
}

/**
 * O que o "Registrar" do ditado já escreveu, por paciente: as células dos
 * módulos da evolução e o texto/registro do Prontuário. Vive como a fila do
 * ditado (memória da aba): sobrevive a trocar de aba da ficha e às várias
 * colunas da ficha, não a recarregar a página — aí nada é trocado, só
 * preenchido (o seguro de sempre).
 */
export interface VoiceRecordMemory {
  fill: ExamFillMap;
  prontuario: { recordId: string | null; text: string } | null;
}

/** Vale só no dia: aba aberta de um dia pro outro não reescreve o registro de ontem. */
const memoria = new Map<string, VoiceRecordMemory & { dia: string }>();

export function getVoiceRecordMemory(patientId: string): VoiceRecordMemory {
  const m = memoria.get(patientId);
  return m && m.dia === todayLocalISO() ? { fill: m.fill, prontuario: m.prontuario } : { fill: {}, prontuario: null };
}

export function setVoiceRecordMemory(patientId: string, patch: Partial<VoiceRecordMemory>): void {
  memoria.set(patientId, { ...getVoiceRecordMemory(patientId), ...patch, dia: todayLocalISO() });
}

/** Logout: nada do ditado de um usuário fica pro próximo. */
export function clearVoiceRecordMemory(): void {
  memoria.clear();
  conversa.clear();
  ouvintesConversa.forEach((fn) => fn());
}

/**
 * Conversa literal no prontuário (07/10/2026, pedido do Leopoldo: "na maioria
 * das vezes não deixarei registrada"). A transcrição do ditado só vira
 * evolução ("Ditado do atendimento (transcrição)") com a chave do rodapé
 * marcada — DESMARCADA por padrão, a cada paciente. O Prontuário estruturado
 * (queixa, HDA, conduta…) não depende dela: sai da fala do mesmo jeito.
 *
 * Por paciente e só no dia, como a memória acima: sobrevive à troca de aba e
 * às várias colunas da ficha. `gravada` = já entrou uma vez neste atendimento
 * (a transcrição entra uma vez só, no primeiro registro com a chave marcada).
 */
export interface ConversaLiteral {
  gravar: boolean;
  gravada: boolean;
}

const conversa = new Map<string, ConversaLiteral & { dia: string }>();
const ouvintesConversa = new Set<() => void>();
const CONVERSA_PADRAO: ConversaLiteral = { gravar: false, gravada: false };

export function getConversaLiteral(patientId: string): ConversaLiteral {
  const c = conversa.get(patientId);
  return c && c.dia === todayLocalISO() ? c : CONVERSA_PADRAO;
}

function setConversa(patientId: string, patch: Partial<ConversaLiteral>): void {
  conversa.set(patientId, { ...getConversaLiteral(patientId), ...patch, dia: todayLocalISO() });
  ouvintesConversa.forEach((fn) => fn());
}

export function setGravarConversaLiteral(patientId: string, gravar: boolean): void {
  setConversa(patientId, { gravar });
}

export function markConversaLiteralGravada(patientId: string): void {
  setConversa(patientId, { gravada: true });
}

export function subscribeConversaLiteral(fn: () => void): () => void {
  ouvintesConversa.add(fn);
  return () => ouvintesConversa.delete(fn);
}

/**
 * O Prontuário do atendimento pode receber o texto do ditado? Só se estiver
 * vazio ou ainda for o que o ditado pôs da última vez — texto que o médico
 * escreveu (ou editou) fica.
 */
export function canReplaceProntuario(current: string, lastFromVoice: string | null): boolean {
  const atual = current.trim();
  return atual === '' || (lastFromVoice !== null && atual === lastFromVoice.trim());
}
